/**
 * 請求計算ロジック
 * プロジェクトの契約タイプ（時給/月額固定）に応じた請求額を計算
 */

import type { Project, TimeRecord } from '@/types';

/**
 * 端数処理
 * @param value 計算結果
 * @param method 処理方法（切り捨て/四捨五入/切り上げ）
 */
export const applyRounding = (
  value: number,
  method: 'floor' | 'round' | 'ceil' = 'floor'
): number => {
  switch (method) {
    case 'floor':
      return Math.floor(value);
    case 'ceil':
      return Math.ceil(value);
    case 'round':
    default:
      return Math.round(value);
  }
};

/**
 * 🆕 明細単位の端数処理（分単位で計算）
 * billingCycle: 15 → 15分単位で端数処理
 * billingCycle: 30 → 30分単位で端数処理
 * @param actualMinutes 実稼働時間（分）
 * @param project プロジェクト設定
 * @returns 端数処理後の時間（時間）
 */
export const calculateBilledHoursForRecord = (
  actualMinutes: number,
  project: Project
): number => {
  if (!project.billingCycle || project.billingCycle === 0) {
    return actualMinutes / 60; // 端数処理なし
  }

  const { billingCycle, calculationMethod = 'floor' } = project;
  let billedMinutes: number;

  switch (calculationMethod) {
    case 'ceil':
      billedMinutes = Math.ceil(actualMinutes / billingCycle) * billingCycle;
      break;
    case 'round':
      billedMinutes = Math.round(actualMinutes / billingCycle) * billingCycle;
      break;
    case 'floor':
    default:
      billedMinutes = Math.floor(actualMinutes / billingCycle) * billingCycle;
  }

  return billedMinutes / 60; // 時間に変換
};

/**
 * 時給計算
 * @param project プロジェクト情報
 * @param totalMinutes 総稼働時間（分）
 * @returns 請求額
 */
export const calculateHourlyBilling = (
  project: Project,
  totalMinutes: number
): number => {
  if (!project.unitPrice) return 0;
  
  const hours = totalMinutes / 60;
  const amount = hours * project.unitPrice;
  
  return applyRounding(amount, project.calculationMethod || 'floor');
};

/**
 * 月額固定計算（レンジ対応）
 * @param project プロジェクト情報
 * @param totalMinutes 総稼働時間（分）
 * @returns { amount: 請求額, isWithinRange: 範囲内か, exceededHours: 超過時間 }
 */
export const calculateMonthlyBilling = (
  project: Project,
  totalMinutes: number
): { amount: number; isWithinRange: boolean; exceededHours?: number } => {
  if (!project.monthlyFixedRate) {
    return { amount: 0, isWithinRange: false };
  }
  
  const hours = totalMinutes / 60;
  const minHours = project.monthlyMinHours || 0;
  const maxHours = project.monthlyMaxHours || Infinity;
  
  // 範囲内チェック（minHours ≤ hours ≤ maxHours）
  if (hours >= minHours && hours <= maxHours) {
    return {
      amount: project.monthlyFixedRate,
      isWithinRange: true,
    };
  }
  
  // 超過時間の計算（maxHours を超えた場合）
  if (hours > maxHours) {
    const exceededHours = hours - maxHours;
    const exceededAmount = project.unitPrice 
      ? exceededHours * project.unitPrice 
      : 0;
    
    return {
      amount: project.monthlyFixedRate + applyRounding(
        exceededAmount, 
        project.calculationMethod || 'floor'
      ),
      isWithinRange: false,
      exceededHours,
    };
  }
  
  // 下限未満（minHours に満たない場合は時給計算）
  return {
    amount: applyRounding(
      hours * (project.unitPrice || 0),
      project.calculationMethod || 'floor'
    ),
    isWithinRange: false,
  };
};

/**
 * プロジェクトの請求額を計算
 * @param project プロジェクト情報
 * @param records タイムレコード配列
 * @returns { totalMinutes: 総時間, totalAmount: 請求額, details: 計算詳細 }
 */
export const calculateProjectBilling = (
  project: Project,
  records: TimeRecord[]
): {
  totalActualMinutes: number;
  totalBilledMinutes: number;
  totalAmount: number;
  details: string;
  recordsWithBilling: Array<TimeRecord & { actualHours: number; billedHours: number }>;
} => {
  let totalActualMinutes = 0;
  let totalBilledMinutes = 0;
  
  // 各レコードに実稼働時間と実績時間を計算
  const recordsWithBilling = records.map((record) => {
    const actualMinutes = record.totalMinutes || 0;
    totalActualMinutes += actualMinutes;
    
    let billedMinutes = actualMinutes;
    
    // 明細単位で端数処理
    if (project.billingAdjustmentType === 'per_item' && project.billingCycle) {
      billedMinutes = calculateBilledHoursForRecord(actualMinutes, project) * 60;
    }
    
    if (project.billingAdjustmentType === 'per_item') {
      totalBilledMinutes += billedMinutes;
    }
    
    return {
      ...record,
      actualHours: actualMinutes / 60,
      billedHours: billedMinutes / 60,
    };
  });
  
  // 月次単位で端数処理
  if (project.billingAdjustmentType === 'monthly_total' || !project.billingAdjustmentType) {
    const billedHours = calculateBilledHoursForRecord(totalActualMinutes, project);
    totalBilledMinutes = billedHours * 60;
  }
  
  const totalActualHours = totalActualMinutes / 60;
  const totalBilledHours = totalBilledMinutes / 60;
  
  // 時給計算
  if (project.contractType === 'hourly') {
    const amount = (totalBilledHours * (project.unitPrice || 0));
    const unitPrice = project.unitPrice?.toLocaleString() || 0;
    
    return {
      totalActualMinutes,
      totalBilledMinutes,
      totalAmount: Math.round(amount),
      details: `時給単価: ¥${unitPrice} × ${totalBilledHours.toFixed(2)}時間`,
      recordsWithBilling,
    };
  }
  
  // 月額固定計算
  if (project.contractType === 'monthly') {
    const result = calculateMonthlyBilling(project, totalActualMinutes);
    const fixedRate = project.monthlyFixedRate?.toLocaleString() || 0;
    
    let details = `月額固定: ¥${fixedRate} (${totalActualHours.toFixed(2)}時間)`;
    
    if (!result.isWithinRange && result.exceededHours) {
      const exceededHours = result.exceededHours.toFixed(2);
      const unitPrice = project.unitPrice?.toLocaleString() || 0;
      details += ` + 超過分: ${exceededHours}時間 (¥${unitPrice}/h)`;
    } else if (!result.isWithinRange && totalActualMinutes < (project.monthlyMinHours || 0) * 60) {
      const unitPrice = project.unitPrice?.toLocaleString() || 0;
      details += ` (下限未満のため時給計算: ¥${unitPrice}/h)`;
    }
    
    return {
      totalActualMinutes,
      totalBilledMinutes,
      totalAmount: result.amount,
      details,
      recordsWithBilling,
    };
  }
  
  // 契約タイプ未設定
  return {
    totalActualMinutes,
    totalBilledMinutes,
    totalAmount: 0,
    details: '契約タイプ未設定',
    recordsWithBilling,
  };
};

/**
 * 計算方法の日本語表示
 */
export const getCalculationMethodLabel = (method?: 'floor' | 'round' | 'ceil'): string => {
  switch (method) {
    case 'floor':
      return '切り捨て';
    case 'ceil':
      return '切り上げ';
    case 'round':
      return '四捨五入';
    default:
      return '未設定';
  }
};

/**
 * 契約タイプの日本語表示
 */
export const getContractTypeLabel = (type?: 'hourly' | 'monthly'): string => {
  switch (type) {
    case 'hourly':
      return '時給';
    case 'monthly':
      return '月額固定';
    default:
      return '未設定';
  }
};
